import { useState, useEffect } from 'react';
import { Link, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  LayoutDashboard, Images, Briefcase, FileText, MessageSquare, 
  Users, Calendar, Settings, LogOut, Menu, X, ChevronDown, FolderOpen, Lock, Shield, Home, BarChart3, Database, Download, Mail, AlertTriangle, ShieldCheck, ShieldAlert, ExternalLink, Briefcase as BriefcaseIcon
} from 'lucide-react';
import { useAdmin } from '../context/AdminContext';
import { Button } from '../components/ui/button';
import { adminFetch } from '../utils/adminFetch';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const menuItems = [
  { icon: LayoutDashboard, label: 'Dashboard', path: '/admin' },
  { icon: BarChart3, label: 'Estadísticas', path: '/admin/analytics' },
  { icon: Home, label: 'Imágenes Inicio', path: '/admin/hero' },
  { icon: Images, label: 'Galerías', path: '/admin/galleries' },
  { icon: Briefcase, label: 'Servicios', path: '/admin/services' },
  { icon: FileText, label: 'Blog', path: '/admin/blog' },
  { icon: MessageSquare, label: 'Testimonios', path: '/admin/testimonials' },
  { icon: Users, label: 'Clientes', path: '/admin/clients' },
  { icon: Calendar, label: 'Calendario', path: '/admin/calendar' },
  { type: 'divider', label: 'Gestión del Negocio' },
  { icon: BriefcaseIcon, label: 'Gestión', externalUrl: 'https://gestion-jon-quintano-photography-205310971860.europe-west2.run.app/' },
  { type: 'divider', label: 'Área de Clientes' },
  { icon: FolderOpen, label: 'Docs Compartidos', path: '/admin/shared-documents' },
  { icon: Lock, label: 'Archivos Privados', path: '/admin/private-files' },
  { icon: Download, label: 'Descargas', path: '/admin/download-logs' },
  { icon: Mail, label: 'Suscriptores', path: '/admin/newsletter' },
  { type: 'divider', label: 'Sistema' },
  { icon: Shield, label: 'Seguridad', path: '/admin/security' },
  { icon: Database, label: 'Migración Datos', path: '/admin/data-migration' },
];

export const AdminLayout = () => {
  const { admin, loading, logout } = useAdmin();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [stats, setStats] = useState(null);
  const [backupStatus, setBackupStatus] = useState(null);
  const [showBackupWarning, setShowBackupWarning] = useState(false);
  const [backupWarningDismissed, setBackupWarningDismissed] = useState(false);

  useEffect(() => {
    if (!loading && !admin) {
      navigate('/admin/login');
    }
  }, [admin, loading, navigate]);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await adminFetch(`${API}/admin/stats`);
        if (response.ok) {
          const data = await response.json();
          setStats(data);
        }
      } catch (error) {
        console.error('Error fetching stats:', error);
      }
    };
    const fetchBackupStatus = async () => {
      try {
        const response = await adminFetch(`${API}/admin/backup-status`);
        if (response.ok) {
          const data = await response.json();
          setBackupStatus(data);
          if (data.needs_backup && !backupWarningDismissed) {
            setShowBackupWarning(true);
          }
        }
      } catch (error) {
        console.error('Error fetching backup status:', error);
      }
    };
    if (admin) {
      fetchStats();
      fetchBackupStatus();
    }
  }, [admin, location.pathname]);

  const handleLogout = async () => {
    await logout();
    navigate('/admin/login');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-foreground border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!admin) return null;

  return (
    <div className="min-h-screen bg-muted">
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-background border-b border-border z-50 flex items-center justify-between px-4">
        <button onClick={() => setSidebarOpen(true)} className="p-2">
          <Menu className="w-6 h-6" />
        </button>
        <span className="font-serif text-lg">Admin Panel</span>
        <button onClick={handleLogout} className="p-2 text-muted-foreground">
          <LogOut className="w-5 h-5" />
        </button>
      </div>

      {/* Sidebar Overlay */}
      {sidebarOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/50 z-50"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed top-0 left-0 h-full w-64 bg-background border-r border-border z-50
        transform transition-transform duration-300 flex flex-col
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        lg:translate-x-0
      `}>
        <div className="p-6 border-b border-border flex items-center justify-between flex-shrink-0">
          <Link to="/" className="font-serif text-xl">Jon Quintano</Link>
          <button onClick={() => setSidebarOpen(false)} className="lg:hidden">
            <X className="w-5 h-5" />
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto p-4 space-y-1">
          {menuItems.map((item, index) => {
            if (item.type === 'divider') {
              return (
                <div key={index} className="pt-4 pb-2">
                  <span className="px-4 text-xs uppercase tracking-widest text-muted-foreground">{item.label}</span>
                </div>
              );
            }
            if (item.externalUrl) {
              return (
                <a
                  key={item.externalUrl}
                  href={item.externalUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setSidebarOpen(false)}
                  className="flex items-center gap-3 px-4 py-3 rounded-lg transition-colors text-muted-foreground hover:bg-muted hover:text-foreground"
                  data-testid={`admin-nav-external-${item.label.toLowerCase()}`}
                >
                  <item.icon className="w-5 h-5" />
                  <span className="text-sm flex-1">{item.label}</span>
                  <ExternalLink className="w-3.5 h-3.5 opacity-60" />
                </a>
              );
            }
            const isActive = location.pathname === item.path || 
              (item.path !== '/admin' && location.pathname.startsWith(item.path));
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                className={`
                  flex items-center gap-3 px-4 py-3 rounded-lg transition-colors
                  ${isActive 
                    ? 'bg-foreground text-background' 
                    : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                  }
                `}
              >
                <item.icon className="w-5 h-5" />
                <span className="text-sm">{item.label}</span>
                {item.label === 'Clientes' && stats?.clients_potential > 0 && (
                  <span className="ml-auto bg-amber-500 text-white text-xs px-2 py-0.5 rounded-full">
                    {stats.clients_potential}
                  </span>
                )}
              </Link>
            );
          })}
        </nav>

        <div className="flex-shrink-0 p-4 border-t border-border">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 bg-muted rounded-full flex items-center justify-center">
              <span className="text-sm font-medium">
                {admin.email?.[0]?.toUpperCase()}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{admin.email}</p>
              <p className="text-xs text-muted-foreground">Administrador</p>
            </div>
          </div>
          {/* Backup Status Indicator */}
          {backupStatus && (
            <div 
              className={`flex items-center gap-2 px-3 py-2 mb-3 rounded text-xs cursor-pointer transition-colors ${
                backupStatus.needs_backup 
                  ? 'bg-red-500/10 text-red-500 hover:bg-red-500/20' 
                  : 'bg-green-500/10 text-green-500 hover:bg-green-500/20'
              }`}
              onClick={() => backupStatus.needs_backup && navigate('/admin/data-migration')}
              data-testid="backup-status-indicator"
            >
              {backupStatus.needs_backup 
                ? <ShieldAlert className="w-4 h-4 flex-shrink-0" /> 
                : <ShieldCheck className="w-4 h-4 flex-shrink-0" />
              }
              <div className="flex flex-col">
                <span>
                  {backupStatus.needs_backup 
                    ? 'Backup pendiente' 
                    : 'Backup al día'
                  }
                </span>
                {backupStatus.last_export_at ? (
                  <span className="opacity-70" style={{ fontSize: '10px' }}>
                    {new Date(backupStatus.last_export_at).toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                  </span>
                ) : (
                  <span className="opacity-70" style={{ fontSize: '10px' }}>Sin backups previos</span>
                )}
              </div>
            </div>
          )}
          <Button 
            variant="outline" 
            size="sm" 
            onClick={handleLogout}
            className="w-full gap-2"
          >
            <LogOut className="w-4 h-4" />
            Cerrar Sesión
          </Button>
        </div>
      </aside>

      {/* Backup Warning Modal */}
      <AnimatePresence>
        {showBackupWarning && (
          <div className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="bg-background border-2 border-red-500/50 max-w-md w-full p-0 shadow-2xl"
              data-testid="backup-warning-modal"
            >
              <div className="bg-red-500 p-4 flex items-center gap-3">
                <AlertTriangle className="w-8 h-8 text-white flex-shrink-0" />
                <div>
                  <h3 className="text-white font-bold text-lg">Copia de seguridad pendiente</h3>
                  <p className="text-red-100 text-sm">Se han realizado cambios sin respaldar</p>
                </div>
              </div>
              <div className="p-6">
                <p className="text-sm text-muted-foreground mb-2">
                  Has añadido o modificado contenido desde tu última copia de seguridad.
                </p>
                {backupStatus?.last_export_at && (
                  <p className="text-xs text-muted-foreground mb-4">
                    Último export: <strong>{new Date(backupStatus.last_export_at).toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</strong>
                  </p>
                )}
                {!backupStatus?.last_export_at && (
                  <p className="text-xs text-amber-500 mb-4">
                    No se ha realizado ninguna copia de seguridad todavía.
                  </p>
                )}
                <div className="flex gap-2">
                  <Button
                    onClick={() => {
                      setShowBackupWarning(false);
                      navigate('/admin/data-migration');
                    }}
                    className="flex-1 bg-red-500 hover:bg-red-600 text-white gap-2"
                    data-testid="backup-go-export"
                  >
                    <Database className="w-4 h-4" />
                    Realizar Export
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowBackupWarning(false);
                      setBackupWarningDismissed(true);
                    }}
                    className="flex-1"
                    data-testid="backup-dismiss"
                  >
                    Más tarde
                  </Button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Main Content */}
      <main className="lg:ml-64 pt-16 lg:pt-0 min-h-screen">
        <Outlet context={{ stats }} />
      </main>
    </div>
  );
};

export default AdminLayout;
